export interface StateCollection {
  state_id: string
  team_id: string
  name: string
}

export interface StateField {
  state_id: string
  team_id: string
  field_name: string
  field_type: FieldType
  description?: string
  default_json_str?: string
  derivation_rule?: string
  choices_json_str?: string
  choices_field_name?: string
  int_min_value?: number
  int_max_value?: number
  may_interject?: boolean
  may_continue_interjections?: string
}

export enum FieldType {
  TEXT = 'TEXT',
  INT = 'INT',
  BOOL = 'BOOL',
  CHOICES = 'CHOICES',
  DATE = 'DATE',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  ZIP_CODE = 'ZIP_CODE'
}

export interface SessionWebhook {
  hook_id: string
  team_id: string
  http_verb: HttpVerb
  url: string
  encoding?: BodyEncoding
  notify_not_ok?: boolean
  webhook_id?: string  // deprecated - use hook_id instead
}

export enum HttpVerb {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH'
}

export enum BodyEncoding {
  JSON = 'JSON',
  FORM_DATA = 'FORM_DATA'
}

export interface SessionHookPayload {
  hook_id: string
  team_id: string
  parameter_type: HookParameterType
  key: string
  value_rule_str: string
  formatter?: ParameterFormatter
  webhook_id?: string  // deprecated - use hook_id instead
}

// Alias for backward compatibility
export type SessionWebhookPayload = SessionHookPayload

export enum HookParameterType {
  QUERY = 'QUERY',
  HEADER = 'HEADER',
  BODY = 'BODY'
}

// Alias for backward compatibility
export const WebhookParameterType = HookParameterType

export enum ParameterFormatter {
  BOOL_TO_YES_NO = 'BOOL_TO_YES_NO',
  PHONE_TO_XXX_XX_XXX = 'PHONE_TO_XXX_XX_XXX',
  DATE_TO_MM_DD_YYYY_SLASH = 'DATE_TO_MM_DD_YYYY_SLASH'
}

export enum HookType {
  WEB = 'WEB',
  SMS = 'SMS'
}

export interface SessionSMSHook {
  hook_id: string
  team_id: string
  agent_address: string
  customer_address: string
  body_template: string
  notify_not_ok?: boolean
}

export interface SessionWebhookRegistry {
  registry_id: string
  hook_id: string
  hook_type: HookType
  team_id: string
  all_sessions?: boolean  // hook runs on all sessions
  route_id?: string       // hook runs on specific route
  agent_id?: string       // hook runs when agent is added
  event: WebhookEvent
  requirement_rule?: string
  webhook_id?: string     // deprecated - use hook_id instead
}

export enum WebhookEvent {
  FINALIZE = 'FINALIZE'
}

// API Response types
export interface StateFieldsResponse {
  state_fields: StateField[]
  state_collections: StateCollection[]
}

export interface SessionWebhooksResponse {
  webhooks: SessionWebhook[]
  sms_hooks: SessionSMSHook[]
  registries: SessionWebhookRegistry[]
  payloads: SessionWebhookPayload[]
}

// Request/Response types for API operations
export interface CreateStateCollectionRequest {
  name: string
  fields?: StateField[]
}

export interface UpdateStateCollectionRequest {
  name?: string
  fields?: StateField[]
}

export interface CreateStateFieldRequest {
  field_name: string
  field_type: FieldType
  description?: string
  default_json_str?: string
  derivation_rule?: string
  choices_json_str?: string
  choices_field_name?: string
  int_min_value?: number
  int_max_value?: number
  may_interject?: boolean
  may_continue_interjections?: string
}

export interface UpdateStateFieldRequest {
  field_name: string
  field_type: FieldType
  description?: string
  default_json_str?: string
  derivation_rule?: string
  choices_json_str?: string
  choices_field_name?: string
  int_min_value?: number
  int_max_value?: number
  may_interject?: boolean
  may_continue_interjections?: string
}

// Session webhook request/response types
export interface CreateSessionWebhookRequest {
  url: string
  http_verb: string
  encoding?: string
  notify_not_ok?: boolean
  registries?: SessionWebhookRegistry[]
  payloads?: SessionWebhookPayload[]
}

export interface UpdateSessionWebhookRequest {
  url?: string
  http_verb?: string
  encoding?: string
  notify_not_ok?: boolean
  registries?: SessionWebhookRegistry[]
  payloads?: SessionWebhookPayload[]
}

export interface PayloadManagementState {
  stateFields: {
    fields: StateField[]
    collections: StateCollection[]
    isLoading: boolean
    error: string | null
  }
  sessionWebhooks: {
    webhooks: SessionWebhook[]
    sms_hooks: SessionSMSHook[]
    registries: SessionWebhookRegistry[]
    payloads: SessionWebhookPayload[]
    isLoading: boolean
    error: string | null
  }
}

// Webhook preview/send request and response types
export interface SendSessionWebhookRequest {
  session_id: string
  webhook_id: string
  state_id?: string
  preview?: boolean
}

export interface SendSessionWebhookResponse {
  encoding: string | null
  http_verb: string
  url: string
  headers: Record<string, string>
  data: any
  status?: number  // HTTP status code when webhook is actually sent (preview=false)
  text?: string    // Response text when webhook is actually sent (preview=false)
}